import React, { useMemo } from 'react';
import { cn } from './cn';

/**
 * Sparkline — tiny SVG line chart for KPI trends.
 * Fully responsive via `preserveAspectRatio="none"` so it stretches to the
 * parent's width while maintaining the same viewBox coordinate system.
 *
 *   <Sparkline values={[3, 7, 5, 12, 9, 14, 11]} tone="brand" />
 */
const TONES = {
  brand: { line: '#2B5BFF', fill: 'rgba(43, 91, 255, 0.12)' },
  success: { line: '#16A34A', fill: 'rgba(22, 163, 74, 0.12)' },
  warning: { line: '#D97706', fill: 'rgba(217, 119, 6, 0.14)' },
  danger: { line: '#DC2626', fill: 'rgba(220, 38, 38, 0.14)' },
  neutral: { line: '#5B6478', fill: 'rgba(91, 100, 120, 0.12)' },
};

export function Sparkline({
  values,
  tone = 'brand',
  width = 120,
  height = 36,
  strokeWidth = 1.5,
  filled = true,
  className,
}) {
  const path = useMemo(() => {
    if (!values || values.length === 0) return null;
    const vs = values.length === 1 ? [values[0], values[0]] : values;
    const min = Math.min(...vs);
    const max = Math.max(...vs);
    const range = max - min || 1;
    const step = width / (vs.length - 1);
    // Pad vertically so the stroke doesn't clip at the box edges.
    const pad = strokeWidth + 1;
    const innerH = height - pad * 2;

    const points = vs.map((v, i) => {
      const x = i * step;
      const y = pad + innerH - ((v - min) / range) * innerH;
      return [x, y];
    });

    const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`).join(' ');
    const area = `${line} L ${width} ${height} L 0 ${height} Z`;
    return { line, area };
  }, [values, width, height, strokeWidth]);

  if (!path) return <div className={cn('h-[36px]', className)} />;

  const c = TONES[tone] || TONES.brand;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden
      className={cn('w-full h-full', className)}
    >
      {filled && <path d={path.area} fill={c.fill} />}
      <path d={path.line} fill="none" stroke={c.line} strokeWidth={strokeWidth} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export default Sparkline;
